import { ClipPayload, ENGINE_KIND, FetchWatcher, plainFetch, SpeakOptions, SpeechEngine } from './speechEngine'
import { wordIndexForCharIndex } from './speechText'

export const CLIP_ERROR = {
  MEDIA: 'media-error',
  PLAY_REJECTED: 'play-rejected',
} as const

const DEFAULT_SENTENCE_GAP_MS = 0
const MIN_GAP_PAUSE_MS = 120
const DEFAULT_HIGHLIGHT_LAG_MS = 0
const MAX_CATCH_UP_WORDS = 4
const MIN_WORD_DWELL_MS = 45

let sentenceGapMs = DEFAULT_SENTENCE_GAP_MS
let highlightLagMs = DEFAULT_HIGHLIGHT_LAG_MS

export const setSentenceGapMs = (ms: number) => { sentenceGapMs = ms }

export const setHighlightLagMs = (ms: number) => { highlightLagMs = ms }
const TIME_TOLERANCE_MS = 5
const MAX_GAP_REWIND_MS = 250
const HAVE_METADATA = 1
const PRELOAD_CACHE_SIZE = 8
const MAX_PRELOADS_IN_FLIGHT = 2

interface ClipAudioEngineDeps {
  cancelFrame: (id: number) => void;
  createAudio: () => HTMLAudioElement;
  requestFrame: (callback: () => void) => number;
}

interface Utterance {
  audio: HTMLAudioElement;
  clip: ClipPayload;
  gapTimer: ReturnType<typeof setTimeout> | null;
  gapWordIndex: number;
  options: SpeakOptions;
  started: boolean;
  highlightedAtMs: number;
  wordIndex: number;
}

export interface PreloadResult {
  bytes: number | null;
  downloadMs: number;
}

interface PreloadOptions {
  deadlineMs?: number;
  watch?: FetchWatcher;
}

interface QueuedPreload {
  deadlineMs?: number;
  resolve: (result: PreloadResult | null) => void;
  url: string;
  watch: FetchWatcher;
}

export interface ClipAudioEngine extends SpeechEngine {
  preload(url: string, options?: PreloadOptions): Promise<PreloadResult | null>;
  unload(): void;
}

const defaultDeps: ClipAudioEngineDeps = {
  cancelFrame: id => window.cancelAnimationFrame(id),
  createAudio: () => new Audio(),
  requestFrame: callback => window.requestAnimationFrame(callback),
}

const supportsAudio = () => typeof window !== 'undefined' && 'HTMLAudioElement' in window

function withinDeadline<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve(null), Math.max(ms, 0))
    promise.then((value) => { clearTimeout(timer); resolve(value) }, (error) => { clearTimeout(timer); reject(error) })
  })
}

export function createClipAudioEngine(overrides: Partial<ClipAudioEngineDeps> = {}): ClipAudioEngine {
  const deps = { ...defaultDeps, ...overrides, }
  const preloaded = new Map<string, string>()
  const preloading = new Set<string>()
  const waiting: QueuedPreload[] = []
  let activePreloads = 0
  let audio: HTMLAudioElement | null = null
  let loadedSource: string | null = null
  let current: Utterance | null = null
  let frameId: number | null = null

  const stopFrames = () => {
    if (frameId === null) { return }

    deps.cancelFrame(frameId)
    frameId = null
  }

  const clearGap = (utterance: Utterance) => {
    if (!utterance.gapTimer) { return }

    clearTimeout(utterance.gapTimer)
    utterance.gapTimer = null
  }

  const finish = (utterance: Utterance) => {
    if (current !== utterance) { return }

    clearGap(utterance)
    stopFrames()
    current = null
    if (utterance.options.onEnd) { utterance.options.onEnd() }
  }

  const fail = (utterance: Utterance, reason: string) => {
    if (current !== utterance) { return }

    if (utterance.options.onError) { utterance.options.onError(reason) }
    finish(utterance)
  }

  const playAudio = (utterance: Utterance) => {
    const result = utterance.audio.play()
    if (result && typeof result.catch === 'function') {
      result.catch(() => fail(utterance, CLIP_ERROR.PLAY_REJECTED))
    }
  }

  const advanceHighlight = (utterance: Utterance, timeMs: number) => {
    const { timeline, words, } = utterance.clip
    let index = utterance.wordIndex
    while (index >= 0 && words[index][0] > timeMs) { index -= 1 }
    while (index + 1 < words.length && words[index + 1][0] <= timeMs) { index += 1 }
    if (index === utterance.wordIndex || index < 0) { return }

    const behind = index - utterance.wordIndex
    if (utterance.wordIndex >= 0 && behind > 1 && behind <= MAX_CATCH_UP_WORDS) {
      if (timeMs - utterance.highlightedAtMs < MIN_WORD_DWELL_MS) { return }

      index = utterance.wordIndex + 1
    }

    utterance.wordIndex = index
    utterance.highlightedAtMs = timeMs
    if (utterance.options.onBoundary) { utterance.options.onBoundary(timeline[index].start) }
  }

  const nextSentenceEnd = (utterance: Utterance) => utterance.clip.sentenceEnds.find(index => index > utterance.gapWordIndex && index + 1 < utterance.clip.words.length)

  const applySentenceGap = (utterance: Utterance, timeMs: number) => {
    const index = nextSentenceEnd(utterance)
    if (index === undefined) { return false }

    const { words, } = utterance.clip
    const endMs = words[index][1]
    if (timeMs < endMs) { return false }

    utterance.gapWordIndex = index
    const nextStartMs = words[index + 1][0]
    const extraMs = sentenceGapMs - (nextStartMs - endMs)
    if (extraMs < MIN_GAP_PAUSE_MS || timeMs > nextStartMs + MAX_GAP_REWIND_MS) { return false }

    advanceHighlight(utterance, words[index][0])
    if (timeMs > nextStartMs) { utterance.audio.currentTime = nextStartMs / 1000 }
    utterance.audio.pause()
    utterance.gapTimer = setTimeout(() => {
      utterance.gapTimer = null
      if (current === utterance) { playAudio(utterance) }
    }, extraMs / (utterance.audio.playbackRate || 1))
    return true
  }

  const tick = () => {
    frameId = null
    const utterance = current
    if (!utterance || utterance.audio.paused) { return }

    const timeMs = utterance.audio.currentTime * 1000 + TIME_TOLERANCE_MS
    if (applySentenceGap(utterance, timeMs)) { return }

    advanceHighlight(utterance, timeMs - highlightLagMs)
    if (current === utterance && !utterance.audio.paused) { scheduleFrame() }
  }

  const scheduleFrame = () => {
    if (frameId === null) { frameId = deps.requestFrame(tick) }
  }

  const handlePlaying = () => {
    const utterance = current
    if (!utterance) { return }

    if (!utterance.started) {
      utterance.started = true
      if (utterance.options.onStart) { utterance.options.onStart() }
    }
    scheduleFrame()
  }

  const handleWaiting = () => {
    const utterance = current
    if (utterance?.started && utterance.options.onStall) { utterance.options.onStall() }
  }

  const ensureAudio = () => {
    if (audio) { return audio }

    audio = deps.createAudio()
    audio.preload = 'auto'
    if ('preservesPitch' in audio) {
      audio.preservesPitch = true
    } else if ('webkitPreservesPitch' in audio) {
      (audio as HTMLAudioElement & { webkitPreservesPitch: boolean }).webkitPreservesPitch = true
    }
    audio.addEventListener('playing', handlePlaying)
    audio.addEventListener('waiting', handleWaiting)
    audio.addEventListener('ended', () => { if (current) { finish(current) } })
    audio.addEventListener('error', () => { if (current) { fail(current, CLIP_ERROR.MEDIA) } })
    return audio
  }

  const seekToWord = (utterance: Utterance, wordIndex: number) => {
    const span = utterance.clip.words[wordIndex]
    if (!span) { return }

    const seek = () => {
      if (current === utterance) { utterance.audio.currentTime = span[0] / 1000 }
    }
    if (utterance.audio.readyState >= HAVE_METADATA) {
      seek()
    } else {
      utterance.audio.addEventListener('loadedmetadata', seek, { once: true, })
    }
    utterance.wordIndex = wordIndex - 1
    utterance.gapWordIndex = wordIndex - 1
  }

  const cancel = () => {
    const utterance = current
    current = null
    stopFrames()
    if (utterance) { clearGap(utterance) }
    if (audio && !audio.paused) { audio.pause() }
  }

  const speak = (text: string, options: SpeakOptions): object | null => {
    const { clip, } = options
    if (!supportsAudio() || !clip) { return null }

    cancel()
    const utterance: Utterance = { audio: ensureAudio(), clip, gapTimer: null, gapWordIndex: -1, highlightedAtMs: Number.NEGATIVE_INFINITY, options, started: false, wordIndex: -1, }
    current = utterance
    const source = preloaded.get(clip.url) || clip.url
    if (loadedSource !== source) {
      utterance.audio.src = source
      loadedSource = source
    }
    utterance.audio.playbackRate = options.rate ?? 1
    seekToWord(utterance, wordIndexForCharIndex(clip.timeline, options.startCharIndex ?? 0) ?? 0)
    playAudio(utterance)
    return utterance
  }

  const pause = () => {
    if (!current) { return false }

    clearGap(current)
    current.audio.pause()
    return true
  }

  const resume = () => {
    if (!current) { return false }

    playAudio(current)
    return true
  }

  const seekToChar = (charIndex: number) => {
    if (!current) { return false }

    const wordIndex = wordIndexForCharIndex(current.clip.timeline, charIndex)
    if (wordIndex === null) { return false }

    clearGap(current)
    seekToWord(current, wordIndex)
    return true
  }

  const setRate = (rate: number) => {
    if (!current) { return false }

    current.audio.playbackRate = rate
    return true
  }

  const forgetPreloaded = (url: string) => {
    const objectUrl = preloaded.get(url)
    if (objectUrl === undefined) { return }

    URL.revokeObjectURL(objectUrl)
    preloaded.delete(url)
  }

  const evictPreloaded = () => {
    preloaded.forEach((objectUrl, url) => {
      if (preloaded.size <= PRELOAD_CACHE_SIZE || url === current?.clip.url || objectUrl === loadedSource) { return }

      forgetPreloaded(url)
    })
  }

  const download = async ({ deadlineMs, url, watch, }: QueuedPreload): Promise<PreloadResult | null> => {
    const requestedAt = Date.now()
    try {
      const response = await watch(url)
      if (!response.ok) { return null }

      const blob = deadlineMs === undefined ? await response.blob() : await withinDeadline(response.blob(), deadlineMs - (Date.now() - requestedAt))
      if (blob === null) { return { bytes: null, downloadMs: Date.now() - requestedAt, } }

      preloaded.set(url, URL.createObjectURL(blob))
      evictPreloaded()
      return { bytes: blob.size, downloadMs: Date.now() - requestedAt, }
    } catch (error) {
      return null
    }
  }

  const startNextPreload = () => {
    if (activePreloads >= MAX_PRELOADS_IN_FLIGHT) { return }

    const next = waiting.shift()
    if (!next) { return }

    activePreloads += 1
    download(next).then((result) => {
      activePreloads -= 1
      preloading.delete(next.url)
      next.resolve(result)
      startNextPreload()
    })
  }

  const preload = (url: string, { deadlineMs, watch = plainFetch }: PreloadOptions = {}): Promise<PreloadResult | null> => {
    if (!supportsAudio() || typeof fetch !== 'function' || preloaded.has(url) || preloading.has(url)) { return Promise.resolve(null) }

    preloading.add(url)
    return new Promise((resolve) => {
      waiting.push({ deadlineMs, resolve, url, watch, })
      startNextPreload()
    })
  }

  const unload = () => {
    waiting.splice(0).forEach((queued) => {
      preloading.delete(queued.url)
      queued.resolve(null)
    })
    preloaded.forEach((objectUrl, url) => { if (objectUrl !== loadedSource) { forgetPreloaded(url) } })
    if (current || !audio) { return }

    preloaded.forEach((_, url) => forgetPreloaded(url))
    audio.removeAttribute('src')
    audio.load()
    loadedSource = null
  }

  return { cancel, isSupported: supportsAudio, kind: ENGINE_KIND.CLIP, pause, preload, resume, seekToChar, setRate, speak, unload, }
}

export const clipAudioEngine = createClipAudioEngine()
